/**
 * Kalenderdagen en planning in Europe/Amsterdam (US-016, US-017).
 *
 * De applicatie redeneert consequent in Nederlandse lokale tijd. De dagelijkse controle draait
 * om 00:15 lokale tijd, ook rond de overgang naar zomer- of wintertijd. Dagrecords hangen aan
 * een kalenderdatum, niet aan een UTC-tijdstip.
 */
export const APP_TIMEZONE = 'Europe/Amsterdam';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Kalenderdatum in Europe/Amsterdam als "YYYY-MM-DD". */
export function toLocalDateKey(instant = new Date(), timeZone = APP_TIMEZONE) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(instant);
    return parts;
}
/** Zet "YYYY-MM-DD" om naar het Date-object dat Prisma voor een `@db.Date`-kolom verwacht. */
export function dateKeyToUtcDate(key) {
    if (!ISO_DATE.test(key))
        throw new Error(`Ongeldige datumsleutel: ${key}`);
    return new Date(`${key}T00:00:00.000Z`);
}
export function utcDateToDateKey(date) {
    return date.toISOString().slice(0, 10);
}
/** Verschuift een datumsleutel met een aantal dagen. */
export function addDays(key, days) {
    const d = dateKeyToUtcDate(key);
    d.setUTCDate(d.getUTCDate() + days);
    return utcDateToDateKey(d);
}
/** Oplopende reeks datumsleutels van `from` tot en met `to`. */
export function dateRange(from, to) {
    const out = [];
    let cursor = from;
    // Bovengrens voorkomt een oneindige lus bij een omgekeerd bereik.
    for (let i = 0; i < 10_000 && cursor <= to; i += 1) {
        out.push(cursor);
        cursor = addDays(cursor, 1);
    }
    return out;
}
const WEEKDAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
/** Korte NL-weergave van een kalenderdag, bv. "wo 27-07". */
export function formatDayLabel(key) {
    const d = dateKeyToUtcDate(key);
    const day = WEEKDAYS[d.getUTCDay()] ?? '';
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    return `${day} ${dd}-${mm}`;
}
/** Volledige NL-datumweergave, bv. "27-07-2026". */
export function formatDateNl(key) {
    const [y, m, d] = key.split('-');
    return `${d}-${m}-${y}`;
}
/** Tijdweergave "00:15" in Europe/Amsterdam. */
export function formatTimeNl(instant, timeZone = APP_TIMEZONE) {
    return new Intl.DateTimeFormat('nl-NL', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(instant);
}
/** Duur in "m:ss"-vorm voor het takenoverzicht. */
export function formatDuration(ms) {
    if (ms === null || ms === undefined)
        return '';
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
/**
 * Offset van een tijdzone ten opzichte van UTC, in minuten, op een gegeven moment.
 * Nodig om een lokaal wandkloktijdstip naar een echt instant om te rekenen zonder
 * externe tijdzonebibliotheek.
 */
function timeZoneOffsetMinutes(instant, timeZone) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
    const parts = Object.fromEntries(dtf.formatToParts(instant).map((p) => [p.type, p.value]));
    const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour) === 24 ? 0 : Number(parts.hour), Number(parts.minute), Number(parts.second));
    return (asUtc - instant.getTime()) / 60_000;
}
/**
 * Het eerstvolgende moment waarop het in `timeZone` `hour:minute` is, gerekend vanaf `from`.
 *
 * De offset wordt tweemaal bepaald omdat die rond een DST-overgang kan verschillen tussen het
 * uitgangsmoment en het doelmoment; zo blijft de controle op 00:15 lokale tijd staan (US-016).
 */
export function nextLocalTime(hour, minute, from = new Date(), timeZone = APP_TIMEZONE) {
    const dateKey = toLocalDateKey(from, timeZone);
    const candidateFor = (key) => {
        const naiveUtc = new Date(`${key}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00.000Z`);
        let guess = new Date(naiveUtc.getTime() - timeZoneOffsetMinutes(naiveUtc, timeZone) * 60_000);
        // Tweede ronde: corrigeer met de offset zoals die op het geschatte moment werkelijk geldt.
        guess = new Date(naiveUtc.getTime() - timeZoneOffsetMinutes(guess, timeZone) * 60_000);
        return guess;
    };
    const today = candidateFor(dateKey);
    if (today.getTime() > from.getTime())
        return today;
    return candidateFor(addDays(dateKey, 1));
}
//# sourceMappingURL=dates.js.map